import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Search, Filter, ChevronRight, AlertCircle, Clock, 
  CheckCircle, User, Building, Plus, Trash2,
  Edit3, X, Save, AlertTriangle, Eye
} from 'lucide-react'
import { supabase } from '../../../lib/supabase'
import { useAuth } from '../../../contexts/AuthContext'
import { toast } from 'sonner'
import { NoteGestionnaireCell } from '../../../components/sinistre/NoteGestionnaireCell'
import DeclarationFlotteModal from '../../../components/sinistre/DeclarationFlotteModal'
import FlagAttenteCell from '../../../components/sinistre/FlagAttenteCell'
import InlineSelectCell from '../../../components/sinistre/InlineSelectCell'
import InlineDateCell from '../../../components/sinistre/InlineDateCell'
import BadgesCell from '../../../components/sinistre/BadgesCell'

const API = process.env.REACT_APP_BACKEND_URL

function InlineEditLieu({ sinistre, onUpdated }) {
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(sinistre.lieu || '')

  async function handleSave() {
    if (val === (sinistre.lieu || '')) { setEditing(false); return }
    try {
      const r = await fetch(`${API}/api/sinistres/${sinistre.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lieu: val }),
      })
      if (r.ok) { toast.success('Lieu mis a jour'); setEditing(false); onUpdated?.() }
    } catch { toast.error('Erreur') }
  }

  if (editing) {
    return (
      <input type="text" value={val} onChange={e => setVal(e.target.value)}
        className="w-full px-1.5 py-0.5 text-xs border border-indigo-300 rounded focus:ring-1 focus:ring-indigo-400"
        autoFocus onBlur={handleSave} onClick={e => e.stopPropagation()}
        onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setVal(sinistre.lieu || ''); setEditing(false) } }}
        data-testid={`inline-lieu-${sinistre.id}`}
      />
    )
  }

  return (
    <span className="text-xs text-slate-700 cursor-pointer hover:text-indigo-600" onClick={e => { e.stopPropagation(); setEditing(true) }}>
      {sinistre.lieu || <span className="text-slate-300 italic">Saisir lieu</span>}
    </span>
  )
}

const STATUT_CONFIG = {
  // New 9-stage pipeline
  declare_c2:                    { label: 'Declare C2',          color: 'bg-slate-100 text-slate-700',   dot: 'bg-slate-400' },
  attente_elements:              { label: 'Att. elements',       color: 'bg-yellow-100 text-yellow-800', dot: 'bg-yellow-500' },
  attente_ouverture_compagnie:   { label: 'Att. ouv. cie',       color: 'bg-cyan-100 text-cyan-700',     dot: 'bg-cyan-500' },
  ouvert_compagnie:              { label: 'Ouvert cie',          color: 'bg-blue-100 text-blue-700',     dot: 'bg-blue-500' },
  attente_compagnie_client:      { label: 'Att. cie/client',     color: 'bg-amber-100 text-amber-700',   dot: 'bg-amber-500' },
  expertise_ou_sans:             { label: 'Expertise',           color: 'bg-purple-100 text-purple-700', dot: 'bg-purple-500' },
  attente_reglement:             { label: 'Att. reglement',      color: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500' },
  reglement_effectue:            { label: 'Regle',               color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  cloture:                       { label: 'Cloture',             color: 'bg-green-100 text-green-800',   dot: 'bg-green-600' },
  // Legacy
  declare:               { label: 'Declare',            color: 'bg-slate-100 text-slate-700',   dot: 'bg-slate-400' },
  en_attente_elements:   { label: 'Attente elements',   color: 'bg-yellow-100 text-yellow-800', dot: 'bg-yellow-500' },
  en_instruction:        { label: 'En instruction',     color: 'bg-blue-100 text-blue-700',     dot: 'bg-blue-500' },
  ouvert_assureur:       { label: 'Ouvert assureur',    color: 'bg-sky-100 text-sky-700',       dot: 'bg-sky-500' },
  en_attente_compagnie:  { label: 'Attente compagnie',  color: 'bg-amber-100 text-amber-700',   dot: 'bg-amber-500' },
  expertise_en_cours:    { label: 'Expertise',          color: 'bg-purple-100 text-purple-700', dot: 'bg-purple-500' },
  negociation:           { label: 'Negociation',        color: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500' },
  accord_recu:           { label: 'Accord recu',        color: 'bg-lime-100 text-lime-700',     dot: 'bg-lime-500' },
  paye:                  { label: 'Paye',               color: 'bg-emerald-50 text-emerald-600',dot: 'bg-emerald-400' },
  cloture_favorable:     { label: 'Cloture OK',         color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  cloture_refus:         { label: 'Refuse',             color: 'bg-red-100 text-red-700',       dot: 'bg-red-500' },
  sans_suite:            { label: 'Sans suite',         color: 'bg-slate-100 text-slate-500',   dot: 'bg-slate-300' },
}

const GRAVITE_CONFIG = {
  faible:   { label: 'Faible',   color: 'text-green-600 bg-green-50' },
  moyen:    { label: 'Moyen',    color: 'text-amber-600 bg-amber-50' },
  eleve:    { label: 'Eleve',    color: 'text-orange-600 bg-orange-50' },
  critique: { label: 'Critique', color: 'text-red-600 bg-red-50' },
}

const MODE_GESTION_CONFIG = {
  assurance: { label: 'Assurance', color: 'bg-blue-100 text-blue-700' },
  amiable:   { label: 'Amiable',   color: 'bg-teal-100 text-teal-700' },
  retention: { label: 'Retention', color: 'bg-slate-100 text-slate-600' },
}

// Menu statut simplifie (liste flotte) -> statuts du pipeline
const STATUT_FLOTTE_OPTIONS = [
  { value: 'attente_ouverture_compagnie', label: 'En attente ouv. CIE', color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  { value: 'attente_elements', label: 'Attente éléments', color: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500' },
  { value: 'ouvert_compagnie', label: 'En cours', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  { value: 'bloque', label: 'Bloqué', color: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
  { value: 'cloture', label: 'Clos', color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  { value: 'cloture_refus', label: 'Refusé', color: 'bg-slate-200 text-slate-700', dot: 'bg-slate-500' },
]

const RESPONSABILITE_ACTEE_OPTIONS = [
  { value: null, label: '--', color: 'bg-slate-50 text-slate-400', dot: '' },
  { value: 'responsable', label: 'Responsable', color: 'bg-red-50 text-red-700', dot: 'bg-red-400' },
  { value: 'non_responsable', label: 'Non resp.', color: 'bg-green-50 text-green-700', dot: 'bg-green-400' },
  { value: 'partage', label: 'Partage', color: 'bg-amber-50 text-amber-700', dot: 'bg-amber-400' },
  { value: 'sans_suite', label: 'Sans suite', color: 'bg-slate-100 text-slate-600', dot: 'bg-slate-400' },
]

function fmt(n) {
  if (n == null) return '\u2014'
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n)
}

function fmtDate(d) {
  if (!d) return '\u2014'
  return new Date(d).toLocaleDateString('fr-FR')
}

// ═══════════════════════════════════════════════════════
// EDIT MODAL
// ═══════════════════════════════════════════════════════

function EditSinistreModal({ sinistre, onClose, onSaved }) {
  const [form, setForm] = useState({
    provision_compagnie: sinistre.provision_compagnie ?? '',
    provision_source: sinistre.provision_source ?? '',
    franchise_estimee: sinistre.franchise_estimee ?? '',
    mode_gestion: sinistre.mode_gestion ?? '',
    responsabilite: sinistre.responsabilite ?? '',
    convention: sinistre.convention ?? '',
    montant_rembourse: sinistre.montant_rembourse ?? '',
    montant_dommages_estime: sinistre.montant_dommages_estime ?? '',
    qualite_donnee: sinistre.qualite_donnee ?? 'a_verifier',
  })
  const [saving, setSaving] = useState(false)

  function handleChange(field, value) {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  async function handleSave() {
    setSaving(true)
    try {
      const updates = {}
      for (const [key, val] of Object.entries(form)) {
        if (val === '') {
          // Send null for empty numeric fields
          if (['provision_compagnie', 'franchise_estimee', 'montant_rembourse', 'montant_dommages_estime'].includes(key)) {
            if (sinistre[key] != null) updates[key] = null
          } else if (val !== (sinistre[key] ?? '')) {
            updates[key] = null
          }
        } else {
          const numFields = ['provision_compagnie', 'franchise_estimee', 'montant_rembourse', 'montant_dommages_estime']
          const parsed = numFields.includes(key) ? parseFloat(val) : val
          if (parsed !== sinistre[key]) updates[key] = parsed
        }
      }

      if (Object.keys(updates).length === 0) {
        toast.info('Aucune modification')
        onClose()
        return
      }

      const res = await fetch(`${API}/api/sinistres/${sinistre.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      })
      const data = await res.json()
      if (data.success) {
        toast.success('Sinistre mis a jour')
        onSaved()
      } else {
        toast.error(data.detail || 'Erreur')
      }
    } catch (e) {
      toast.error('Erreur reseau')
    } finally {
      setSaving(false)
    }
  }

  const inputCls = "w-full h-9 px-3 border border-slate-200 rounded-md text-sm bg-white outline-none focus:ring-2 focus:ring-indigo-500"
  const selectCls = "w-full h-9 px-3 border border-slate-200 rounded-md text-sm bg-white outline-none focus:ring-2 focus:ring-indigo-500"
  const labelCls = "block text-xs font-semibold text-slate-600 mb-1"

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="edit-sinistre-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-slate-900">Editer le sinistre</h2>
            <p className="text-xs text-slate-500 font-mono">{sinistre.numero_sinistre_client || sinistre.numero_sinistre}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="edit-modal-close">
            <X size={18} className="text-slate-400" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Provision compagnie */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Provision compagnie</label>
              <input
                type="number" step="0.01"
                value={form.provision_compagnie}
                onChange={e => handleChange('provision_compagnie', e.target.value)}
                className={inputCls}
                placeholder="Montant provision"
                data-testid="edit-provision-compagnie"
              />
            </div>
            <div>
              <label className={labelCls}>Source provision</label>
              <select
                value={form.provision_source}
                onChange={e => handleChange('provision_source', e.target.value)}
                className={selectCls}
                data-testid="edit-provision-source"
              >
                <option value="">-- Non defini --</option>
                <option value="releve_compagnie">Releve compagnie</option>
                <option value="saisie_manuelle">Saisie manuelle</option>
                <option value="import_excel">Import Excel</option>
                <option value="estimation">Estimation</option>
              </select>
            </div>
          </div>

          {/* Franchise + Mode gestion */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Franchise estimee</label>
              <input
                type="number" step="0.01"
                value={form.franchise_estimee}
                onChange={e => handleChange('franchise_estimee', e.target.value)}
                className={inputCls}
                placeholder="Reste a charge"
                data-testid="edit-franchise-estimee"
              />
            </div>
            <div>
              <label className={labelCls}>Mode de gestion</label>
              <select
                value={form.mode_gestion}
                onChange={e => handleChange('mode_gestion', e.target.value)}
                className={selectCls}
                data-testid="edit-mode-gestion"
              >
                <option value="">-- Non defini --</option>
                <option value="assurance">Assurance</option>
                <option value="amiable">Amiable</option>
                <option value="retention">Retention</option>
              </select>
            </div>
          </div>

          {/* Responsabilite + Convention */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Responsabilite</label>
              <select
                value={form.responsabilite}
                onChange={e => handleChange('responsabilite', e.target.value)}
                className={selectCls}
                data-testid="edit-responsabilite"
              >
                <option value="">-- Non defini --</option>
                <option value="responsable">Responsable</option>
                <option value="non_responsable">Non responsable</option>
                <option value="partage_50_50">Partage 50/50</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Convention</label>
              <select
                value={form.convention}
                onChange={e => handleChange('convention', e.target.value)}
                className={selectCls}
                data-testid="edit-convention"
              >
                <option value="">-- Non defini --</option>
                <option value="irsa">IRSA</option>
                <option value="irca">IRCA</option>
                <option value="hors_convention">Hors convention</option>
              </select>
            </div>
          </div>

          {/* Montants */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Montant rembourse</label>
              <input
                type="number" step="0.01"
                value={form.montant_rembourse}
                onChange={e => handleChange('montant_rembourse', e.target.value)}
                className={inputCls}
                placeholder="Rembourse par assureur"
                data-testid="edit-montant-rembourse"
              />
            </div>
            <div>
              <label className={labelCls}>Dommages estimes</label>
              <input
                type="number" step="0.01"
                value={form.montant_dommages_estime}
                onChange={e => handleChange('montant_dommages_estime', e.target.value)}
                className={inputCls}
                placeholder="Estimation dommages"
                data-testid="edit-montant-dommages-estime"
              />
            </div>
          </div>

          {/* Qualite */}
          <div>
            <label className={labelCls}>Qualite de la donnee</label>
            <select
              value={form.qualite_donnee}
              onChange={e => handleChange('qualite_donnee', e.target.value)}
              className={selectCls}
              data-testid="edit-qualite-donnee"
            >
              <option value="a_verifier">A verifier</option>
              <option value="verifie">Verifie</option>
              <option value="incomplet">Incomplet</option>
              <option value="incoherent">Incoherent</option>
            </select>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg" data-testid="edit-cancel-btn">
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            data-testid="edit-save-btn"
          >
            <Save size={14} />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  )
}


// ═══════════════════════════════════════════════════════
// BULK EDIT MODAL
// ═══════════════════════════════════════════════════════

function BulkEditModal({ ids, onClose, onSaved }) {
  const [form, setForm] = useState({
    mode_gestion: '',
    responsabilite: '',
    convention: '',
    qualite_donnee: '',
  })
  const [saving, setSaving] = useState(false)

  async function handleSave() {
    const updates = {}
    for (const [k, v] of Object.entries(form)) {
      if (v) updates[k] = v
    }
    if (Object.keys(updates).length === 0) {
      toast.info('Selectionnez au moins un champ')
      return
    }
    setSaving(true)
    try {
      const res = await fetch(`${API}/api/sinistres/bulk-action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: [...ids], action: 'modifier', updates }),
      })
      const data = await res.json()
      if (data.success) {
        toast.success(`${data.results?.success || ids.length} sinistre(s) mis a jour`)
        onSaved()
      } else {
        toast.error(data.detail || 'Erreur')
      }
    } catch {
      toast.error('Erreur reseau')
    } finally {
      setSaving(false)
    }
  }

  const selectCls = "w-full h-9 px-3 border border-slate-200 rounded-md text-sm bg-white outline-none focus:ring-2 focus:ring-indigo-500"

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="bulk-edit-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-slate-900">Modification en masse</h2>
            <p className="text-xs text-slate-500">{ids.length} sinistre(s) selectionne(s)</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
            <X size={18} className="text-slate-400" />
          </button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <p className="text-xs text-amber-600 bg-amber-50 rounded-lg p-3">
            Seuls les champs remplis seront appliques a tous les sinistres selectionnes.
          </p>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Mode de gestion</label>
            <select value={form.mode_gestion} onChange={e => setForm(f => ({ ...f, mode_gestion: e.target.value }))} className={selectCls} data-testid="bulk-mode-gestion">
              <option value="">-- Ne pas modifier --</option>
              <option value="assurance">Assurance</option>
              <option value="amiable">Amiable</option>
              <option value="retention">Retention</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Responsabilite</label>
            <select value={form.responsabilite} onChange={e => setForm(f => ({ ...f, responsabilite: e.target.value }))} className={selectCls} data-testid="bulk-responsabilite">
              <option value="">-- Ne pas modifier --</option>
              <option value="responsable">Responsable</option>
              <option value="non_responsable">Non responsable</option>
              <option value="partage_50_50">Partage 50/50</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Convention</label>
            <select value={form.convention} onChange={e => setForm(f => ({ ...f, convention: e.target.value }))} className={selectCls} data-testid="bulk-convention">
              <option value="">-- Ne pas modifier --</option>
              <option value="irsa">IRSA</option>
              <option value="irca">IRCA</option>
              <option value="hors_convention">Hors convention</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Qualite donnee</label>
            <select value={form.qualite_donnee} onChange={e => setForm(f => ({ ...f, qualite_donnee: e.target.value }))} className={selectCls} data-testid="bulk-qualite">
              <option value="">-- Ne pas modifier --</option>
              <option value="verifie">Verifie</option>
              <option value="a_verifier">A verifier</option>
              <option value="incomplet">Incomplet</option>
              <option value="incoherent">Incoherent</option>
            </select>
          </div>
        </div>
        <div className="border-t border-slate-200 px-6 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Annuler</button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            data-testid="bulk-save-btn"
          >
            <Save size={14} />
            {saving ? 'Application...' : 'Appliquer'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════

export default function C2Sinistres({ forceTenantId = null, forcePoliceId = null }) {
  const navigate = useNavigate()
  const { profile } = useAuth()
  const [sinistres, setSinistres] = useState([])
  const [tenants, setTenants] = useState([])
  const [gestionnaires, setGestionnaires] = useState([])
  const [loading, setLoading] = useState(true)
  
  // Active view tab
  
  // Filters — persistés en session pour survivre à l'aller-retour liste ↔ fiche (le gestionnaire les efface via "Reinitialiser")
  const filtersKey = `c2-sinistres-filtres:${forceTenantId || 'all'}:${forcePoliceId || 'all'}`
  const [savedFilters] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(filtersKey)) || {} } catch { return {} }
  })
  const [search, setSearch] = useState(savedFilters.search ?? '')
  const [filterStatut, setFilterStatut] = useState(savedFilters.filterStatut ?? 'all')
  const [filterTenant, setFilterTenant] = useState(forceTenantId || savedFilters.filterTenant || 'all')
  const [filterGestionnaire, setFilterGestionnaire] = useState(savedFilters.filterGestionnaire ?? 'all')
  const [filterType, setFilterType] = useState(savedFilters.filterType ?? 'all')
  const [filterGravite, setFilterGravite] = useState(savedFilters.filterGravite ?? 'all')
  const [filterModeGestion, setFilterModeGestion] = useState(savedFilters.filterModeGestion ?? 'all')
  const [filterOuvert, setFilterOuvert] = useState(savedFilters.filterOuvert ?? 'all')
  const [filterQualite, setFilterQualite] = useState(savedFilters.filterQualite ?? 'all')
  const [filterResponsabilite, setFilterResponsabilite] = useState(savedFilters.filterResponsabilite ?? 'all')
  const [filterEtatDossier, setFilterEtatDossier] = useState(savedFilters.filterEtatDossier ?? 'all')
  const [filterRespActee, setFilterRespActee] = useState(savedFilters.filterRespActee ?? 'all')
  const [filterAnomalies, setFilterAnomalies] = useState(savedFilters.filterAnomalies ?? false)
  useEffect(() => {
    sessionStorage.setItem(filtersKey, JSON.stringify({
      search, filterStatut, filterTenant, filterGestionnaire, filterType, filterGravite,
      filterModeGestion, filterOuvert, filterQualite, filterResponsabilite, filterEtatDossier, filterRespActee, filterAnomalies,
    }))
  }, [filtersKey, search, filterStatut, filterTenant, filterGestionnaire, filterType, filterGravite, filterModeGestion, filterOuvert, filterQualite, filterResponsabilite, filterEtatDossier, filterRespActee, filterAnomalies])
  const [showPreDeclFlotte, setShowPreDeclFlotte] = useState(false)
  const [policesForImport, setPolicesForImport] = useState([])
  const [selected, setSelected] = useState(new Set())
  const [bulkLoading, setBulkLoading] = useState(false)
  
  // Modals
  const [editingSinistre, setEditingSinistre] = useState(null)
  const [showBulkEdit, setShowBulkEdit] = useState(false)

  const fetchSinistres = useCallback(async () => {
    try {
      const params = new URLSearchParams()
      if (forceTenantId) {
        params.set('tenant_id', forceTenantId)
      } else {
        if (profile?.id) params.set('user_id', profile.id)
        if (profile?.role) params.set('role', profile.role)
      }
      if (forcePoliceId) {
        params.set('police_id', forcePoliceId)
      }
      const res = await fetch(`${API}/api/sinistres?${params}`)
      const data = await res.json()
      const list = data.sinistres || []
      setSinistres(list)
      
      const uniqueTenants = [...new Map(
        list.filter(s => s.tenants).map(s => [s.tenants.id, s.tenants])
      ).values()]
      setTenants(uniqueTenants)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }, [forceTenantId, forcePoliceId, profile])

  useEffect(() => {
    if (profile) {
      fetchSinistres()
      fetchGestionnaires()
      if (forceTenantId) fetchPolices()
    }
  }, [profile, fetchSinistres, forceTenantId])

  async function fetchPolices() {
    try {
      const res = await fetch(`${API}/api/polices?tenant_id=${forceTenantId}`)
      const data = await res.json()
      setPolicesForImport(data.polices || data.data || [])
    } catch {}
  }

  async function fetchGestionnaires() {
    try {
      const { data } = await supabase
        .from('user_profiles')
        .select('id, prenom, nom')
        .in('role', ['c2_admin', 'c2_gestionnaire'])
      setGestionnaires(data || [])
    } catch (e) {
      console.error(e)
    }
  }

  // Apply filters
  const filtered = sinistres.filter(s => {
    if (search.trim()) {
      const norm = v => (v || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      const haystack = norm([
        s.numero_sinistre, s.nature_sinistre, s.tenants?.nom, s.numero_sinistre_client, s.numero_dossier_compagnie,
        s.vehicules?.immatriculation, s.immatriculation_saisie, s.description, s.lieu,
        s.chauffeur_nom_saisi, s.chauffeur_prenom_saisi, s.chauffeurs?.nom, s.chauffeurs?.prenom,
        s.nom_chef_depot, s.tiers_nom, s.tiers_vehicule, s.tiers_assurance,
        s.dommages_client, s.dommages_tiers, s.informations_expertise,
        s.note_gestionnaire, s.note_interne,
      ].filter(Boolean).join(' \n '))
      const words = norm(search).split(/\s+/).filter(Boolean)
      if (!words.every(w => haystack.includes(w))) return false
    }
    if (filterStatut !== 'all' && s.statut !== filterStatut) return false
    if (filterTenant !== 'all' && s.tenants?.id !== filterTenant) return false
    if (filterGestionnaire !== 'all') {
      if (filterGestionnaire === 'none' && s.gestionnaire) return false
      if (filterGestionnaire !== 'none' && s.gestionnaire?.id !== filterGestionnaire) return false
    }
    if (filterType !== 'all' && s.type_sinistre !== filterType) return false
    if (filterGravite !== 'all' && s.gravite !== filterGravite) return false
    if (filterModeGestion !== 'all') {
      const mg = s.mode_gestion || (s.est_amiable ? 'amiable' : 'assurance')
      if (mg !== filterModeGestion) return false
    }
    if (filterResponsabilite !== 'all' && s.responsabilite !== filterResponsabilite) return false
    if (filterEtatDossier !== 'all') {
      if (filterEtatDossier === 'mec_oui' && !s.est_mise_en_cause) return false
      else if (filterEtatDossier === 'mec_non' && s.est_mise_en_cause) return false
      else if (filterEtatDossier === 'cons_oui' && !s.est_conservatoire) return false
      else if (filterEtatDossier === 'cons_non' && s.est_conservatoire) return false
      else if (!filterEtatDossier.startsWith('mec_') && !filterEtatDossier.startsWith('cons_') && s.etat_dossier !== filterEtatDossier) return false
    }
    if (filterRespActee !== 'all' && s.responsabilite_actee !== filterRespActee) return false
    if (filterAnomalies && !(s.anomalies_compagnie?.length)) return false
    if (filterOuvert === 'ouvert' && s.date_cloture) return false
    if (filterOuvert === 'cloture' && !s.date_cloture) return false
    if (filterQualite !== 'all' && s.qualite_donnee !== filterQualite) return false
    return true
  })

  // Fleet mode: detect if we're viewing sinistres from a fleet police (use raw data, not filtered)
  const hasFlottePolice = policesForImport.some(p => p.type_police === 'flotte')
  const isFlotteMode = hasFlottePolice || (sinistres.length > 0 && sinistres.some(s =>
    s.polices?.type_police === 'flotte' || s.type_sinistre === 'flotte'
  ))

  // Stats — CORRECTED: use provision_compagnie, NOT montant_dommages_estime
  const openCount = sinistres.filter(s => !s.date_cloture).length
  const closedCount = sinistres.filter(s => s.date_cloture).length
  const unassignedCount = sinistres.filter(s => !s.gestionnaire && !s.date_cloture).length
  const provisionCompagnie = sinistres
    .filter(s => !s.date_cloture && s.provision_compagnie != null)
    .reduce((sum, s) => sum + (Number(s.provision_compagnie) || 0), 0)
  const nbSansProvision = sinistres.filter(s => !s.date_cloture && s.provision_compagnie == null).length
  const nbEcartsCie = sinistres.filter(s => s.anomalies_compagnie?.length).length

  function toggleSelect(id) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleSelectAll() {
    if (selected.size === filtered.length) setSelected(new Set())
    else setSelected(new Set(filtered.map(s => s.id)))
  }

  async function handleBulkDelete() {
    if (!selected.size) return
    const ok = window.confirm(`Supprimer definitivement ${selected.size} sinistre(s) ? Cette action est irreversible.`)
    if (!ok) return
    setBulkLoading(true)
    try {
      const res = await fetch(`${API}/api/sinistres/bulk-action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: [...selected], action: 'supprimer', user_id: profile?.id }),
      })
      const data = await res.json()
      if (data.success) {
        setSelected(new Set())
        toast.success(`${data.results?.success || selected.size} sinistre(s) supprime(s)`)
        fetchSinistres()
      } else {
        toast.error(data.detail || 'Suppression refusee')
      }
    } catch (e) {
      console.error(e)
      toast.error('Erreur suppression')
    } finally {
      setBulkLoading(false)
    }
  }

  function handleEditDone() {
    setEditingSinistre(null)
    setShowBulkEdit(false)
    setSelected(new Set())
    fetchSinistres()
  }

  // Provision display helper
  function renderProvisionCell(s) {
    if (s.provision_compagnie != null) {
      return (
        <div>
          <span className="text-xs text-slate-800 font-mono">{fmt(s.provision_compagnie)}</span>
          <p className="text-[10px] text-blue-500">Provision cie</p>
        </div>
      )
    }
    if (s.montant_dommages_estime != null && Number(s.montant_dommages_estime) > 0) {
      return (
        <div>
          <span className="text-xs text-slate-600 font-mono">{fmt(s.montant_dommages_estime)}</span>
          <p className="text-[10px] text-amber-500">Dommages estimes</p>
        </div>
      )
    }
    return <span className="text-[10px] text-slate-400">Non renseigne</span>
  }

  return (
    <div className="p-8" data-testid="c2-sinistres">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Sinistres
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {filtered.length} dossier{filtered.length > 1 ? 's' : ''} sur {sinistres.length}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selected.size > 0 && (
            <>
              <button
                onClick={() => setShowBulkEdit(true)}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors"
                data-testid="sinistres-bulk-edit-btn"
              >
                <Edit3 size={14} />
                Modifier ({selected.size})
              </button>
              {['master_admin', 'c2_admin'].includes(profile?.role) && (
                <button
                  onClick={handleBulkDelete}
                  disabled={bulkLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                  data-testid="sinistres-bulk-delete-btn"
                >
                  <Trash2 size={14} />
                  {bulkLoading ? 'Suppression...' : `Supprimer (${selected.size})`}
                </button>
              )}
            </>
          )}
          {isFlotteMode && forceTenantId && (
            <button
              onClick={() => setShowPreDeclFlotte(true)}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 transition-colors"
              data-testid="declaration-flotte-btn"
            >
              <Plus size={14} />
              Déclaration flotte
            </button>
          )}
        </div>
      </div>

      {/* Déclaration flotte directe : fiche ASS / AMI vierge */}
      {showPreDeclFlotte && (
        <DeclarationFlotteModal
          tenantId={forceTenantId}
          policeId={policesForImport.find(p => p.type_police === 'flotte')?.id || null}
          onCreated={(newId) => { setShowPreDeclFlotte(false); fetchSinistres(); navigate(`/c2/sinistre/${newId}`) }}
          onClose={() => setShowPreDeclFlotte(false)}
        />
      )}
      
      {/* Edit modals */}
      {editingSinistre && (
        <EditSinistreModal
          sinistre={editingSinistre}
          onClose={() => setEditingSinistre(null)}
          onSaved={handleEditDone}
        />
      )}
      {showBulkEdit && (
        <BulkEditModal
          ids={[...selected]}
          onClose={() => setShowBulkEdit(false)}
          onSaved={handleEditDone}
        />
      )}

      {/* Stats — CORRECTED */}
      <div className="grid grid-cols-5 gap-3 mb-6">
        {[
          { label: 'En cours', value: openCount, color: 'bg-blue-50 text-blue-700 border-blue-100', active: filterOuvert === 'ouvert', onClick: () => setFilterOuvert('ouvert') },
          { label: 'Clotures', value: closedCount, color: 'bg-emerald-50 text-emerald-700 border-emerald-100', active: filterOuvert === 'cloture', onClick: () => setFilterOuvert('cloture') },
          { label: 'Provisions cie', value: provisionCompagnie > 0 ? fmt(provisionCompagnie) : '\u2014', sub: nbSansProvision > 0 ? `${nbSansProvision} non rens.` : null, color: 'bg-indigo-50 text-indigo-700 border-indigo-100' },
          { label: 'Non assignes', value: unassignedCount, color: 'bg-orange-50 text-orange-700 border-orange-100', onClick: () => setFilterGestionnaire('none') },
          { label: 'Total', value: sinistres.length, color: 'bg-slate-50 text-slate-700 border-slate-100', active: filterOuvert === 'all', onClick: () => setFilterOuvert('all') },
        ].map(({ label, value, sub, color, active, onClick }) => (
          <button
            key={label}
            onClick={onClick || (() => {})}
            className={`rounded-lg px-3 py-3 border transition-all text-left ${color} ${
              active ? 'ring-2 ring-offset-1 ring-slate-400' : ''
            } hover:ring-1 hover:ring-slate-300`}
            data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}
          >
            <p className="text-lg font-bold tabular-nums" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {value}
            </p>
            <p className="text-xs font-medium mt-0.5">{label}</p>
            {sub && <p className="text-[10px] opacity-70">{sub}</p>}
          </button>
        ))}
      </div>

      <>
          {/* Filters */}
          <div className="bg-white rounded-lg border border-slate-200 p-4 mb-5">
            <div className="flex flex-wrap items-center gap-3">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Rechercher un mot : n°, immat, chauffeur, lieu, description, notes..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  data-testid="sinistres-search"
                  className="pl-9 pr-4 h-9 w-full border border-slate-200 rounded-md text-sm bg-white outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Status */}
              <select value={filterStatut} onChange={e => setFilterStatut(e.target.value)}
                className="h-9 px-3 border border-slate-200 rounded-md text-sm bg-white outline-none focus:ring-2 focus:ring-indigo-500"
                data-testid="filter-statut"
              >
                <option value="all">Tous les statuts</option>
                {Object.entries(STATUT_CONFIG).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>

              {/* Mode gestion */}
              <select value={filterModeGestion} onChange={e => setFilterModeGestion(e.target.value)}
                className="h-9 px-3 border border-slate-200 rounded-md text-sm bg-white outline-none focus:ring-2 focus:ring-indigo-500"
                data-testid="filter-mode-gestion"
              >
                <option value="all">Tous les modes</option>
                <option value="assurance">Assurance</option>
                <option value="amiable">Amiable</option>
              </select>

              {/* Responsabilite */}
              <select value={filterResponsabilite} onChange={e => setFilterResponsabilite(e.target.value)}
                className="h-9 px-3 border border-slate-200 rounded-md text-sm bg-white outline-none focus:ring-2 focus:ring-indigo-500"
                data-testid="filter-responsabilite"
              >
                <option value="all">Responsabilite</option>
                <option value="responsable">Responsable</option>
                <option value="non_responsable">Non responsable</option>
                <option value="partage">Partage</option>
              </select>

              {/* Client */}
              {!forceTenantId && (
                <select value={filterTenant} onChange={e => setFilterTenant(e.target.value)}
                  className="h-9 px-3 border border-slate-200 rounded-md text-sm bg-white outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="all">Tous les clients</option>
                  {tenants.map(t => (
                    <option key={t.id} value={t.id}>{t.nom}</option>
                  ))}
                </select>
              )}

              {/* Type */}
              <select value={filterType} onChange={e => setFilterType(e.target.value)}
                className="h-9 px-3 border border-slate-200 rounded-md text-sm bg-white outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="all">Tous les types</option>
                <option value="dab">Dommages aux biens</option>
                <option value="flotte">Flotte automobile</option>
                <option value="rc_tiers">RC Tiers</option>
                <option value="amiable">Amiable</option>
                <option value="contentieux">Contentieux</option>
              </select>

              {/* Qualite */}
              <select value={filterQualite} onChange={e => setFilterQualite(e.target.value)}
                className="h-9 px-3 border border-slate-200 rounded-md text-sm bg-white outline-none focus:ring-2 focus:ring-indigo-500"
                data-testid="filter-qualite"
              >
                <option value="all">Toute qualite</option>
                <option value="a_verifier">A verifier</option>
                <option value="verifie">Verifie</option>
                <option value="incomplet">Incomplet</option>
                <option value="incoherent">Incoherent</option>
              </select>

              {/* Etat dossier (flotte) — fusionne Mode + MEC + CONS */}
              {isFlotteMode && (
                <select value={filterEtatDossier} onChange={e => setFilterEtatDossier(e.target.value)}
                  className="h-9 px-3 border border-slate-200 rounded-md text-sm bg-white outline-none focus:ring-2 focus:ring-indigo-500"
                  data-testid="filter-etat-dossier"
                >
                  <option value="all">MEC / Conservatoire : Tous</option>
                  <option value="mec_oui">MEC uniquement</option>
                  <option value="mec_non">Sans MEC</option>
                  <option value="cons_oui">Conservatoire</option>
                  <option value="cons_non">Non conservatoire</option>
                </select>
              )}

              {/* Resp. actee filter */}
              {isFlotteMode && (
                <select value={filterRespActee} onChange={e => setFilterRespActee(e.target.value)}
                  className="h-9 px-3 border border-slate-200 rounded-md text-sm bg-white outline-none focus:ring-2 focus:ring-indigo-500"
                  data-testid="filter-resp-actee"
                >
                  <option value="all">Resp. actee: Toutes</option>
                  <option value="responsable">Responsable</option>
                  <option value="non_responsable">Non responsable</option>
                  <option value="partage">Partage</option>
                  <option value="sans_suite">Sans suite</option>
                </select>
              )}

              {/* Ecarts compagnie filter (flotte) */}
              {nbEcartsCie > 0 && (
                <button
                  onClick={() => setFilterAnomalies(!filterAnomalies)}
                  className={`h-9 px-3 rounded-md text-xs font-semibold border flex items-center gap-1.5 transition-colors ${
                    filterAnomalies ? 'bg-orange-500 text-white border-orange-500' : 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100'
                  }`}
                  data-testid="filter-ecarts-compagnie"
                >
                  <AlertTriangle size={12} /> Écarts cie ({nbEcartsCie})
                </button>
              )}

              {/* Clear */}
              {(search || filterStatut !== 'all' || filterTenant !== 'all' || filterModeGestion !== 'all' || filterType !== 'all' || filterQualite !== 'all' || filterResponsabilite !== 'all' || filterEtatDossier !== 'all' || filterRespActee !== 'all' || filterAnomalies) && (
                <button
                  onClick={() => {
                    setSearch(''); setFilterStatut('all'); setFilterTenant(forceTenantId || 'all')
                    setFilterGestionnaire('all'); setFilterType('all'); setFilterGravite('all')
                    setFilterModeGestion('all'); setFilterQualite('all'); setFilterResponsabilite('all')
                    setFilterEtatDossier('all'); setFilterRespActee('all'); setFilterAnomalies(false)
                  }}
                  className="text-xs text-slate-500 hover:text-slate-700"
                  data-testid="filter-reset"
                >
                  Reinitialiser
                </button>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-x-auto">
            {loading ? (
              <div className="p-12 text-center">
                <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-sm text-slate-400">Chargement...</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-12 text-center">
                <AlertCircle size={40} className="mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500">Aucun sinistre trouve</p>
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="w-8 px-2 py-2">
                      <input
                        type="checkbox"
                        checked={filtered.length > 0 && selected.size === filtered.length}
                        onChange={toggleSelectAll}
                        className="rounded border-slate-300"
                        data-testid="sinistres-select-all"
                      />
                    </th>
                    {isFlotteMode ? (
                      <>
                        <th className="w-7 px-1 py-2"></th>
                        <th className="text-left px-2 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wide">N Client</th>
                        <th className="text-left px-2 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Badges</th>
                        <th className="text-left px-2 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wide">N Cie / Tiers</th>
                        <th className="text-left px-2 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Date</th>
                        <th className="text-left px-2 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Immat.</th>
                        <th className="text-left px-2 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wide" title="Responsabilité actée (validée compagnie) — se modifie dans la fiche">Actée</th>
                        <th className="text-left px-2 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Notes</th>
                        <th className="text-left px-2 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Expert.</th>
                        <th className="text-left px-2 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Statut</th>
                        <th className="w-8 px-1 py-2"></th>
                      </>
                    ) : (
                      <>
                        {['', 'Lieu', 'N Dossier Cie', 'Client', 'Nature', 'Mode', 'Notes gestionnaire', 'Statut', 'Date', ''].map(h => (
                          <th key={h} className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                        ))}
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {[...filtered].sort((a, b) => isFlotteMode ? 0 : (b.date_fait || '').localeCompare(a.date_fait || '')).map((s, i) => {
                    const st = STATUT_CONFIG[s.statut] || STATUT_CONFIG.declare
                    const mg = MODE_GESTION_CONFIG[s.mode_gestion]
                    
                    if (isFlotteMode) {
                      // Fleet-specific row
                      const immat = (s.vehicules?.immatriculation || s.immatriculation_saisie || '--').replace(/-/g, '')
                      const noteHex = s.note_couleur ? (
                        s.note_couleur === 'jaune' ? '#FCD34D' :
                        s.note_couleur === 'marron' ? '#A16207' :
                        s.note_couleur === 'rose' ? '#F472B6' :
                        s.note_couleur === 'violet' ? '#A78BFA' :
                        s.note_couleur === 'vert' ? '#34D399' :
                        s.note_couleur === 'bleu' ? '#60A5FA' : '#9CA3AF'
                      ) : null
                      const hasExpertise = s.statut === 'expertise_en_cours' || s.date_mission_expert || s.date_rdv_expertise
                      const hasNote = s.note_gestionnaire && s.note_gestionnaire.trim().length > 0
                      const hasEcartCie = s.anomalies_compagnie?.length > 0
                      return (
                        <tr
                          key={s.id}
                          className={`border-b border-slate-100 cursor-pointer transition-colors ${
                            hasEcartCie ? 'bg-orange-50 hover:bg-orange-100/70' : `hover:bg-indigo-50/50 ${i % 2 ? 'bg-slate-50/30' : ''}`
                          } ${selected.has(s.id) ? 'bg-indigo-50' : ''}`}
                          data-testid="sinistre-row-flotte"
                        >
                          <td className="px-2 py-2" onClick={e => e.stopPropagation()}>
                            <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggleSelect(s.id)} className="rounded border-slate-300" />
                          </td>
                          <td className="px-1 py-2" onClick={e => e.stopPropagation()}>
                            <FlagAttenteCell sinistre={s} onUpdated={fetchSinistres} />
                          </td>
                          <td className="px-2 py-2" onClick={() => navigate(`/c2/sinistre/${s.id}`)}>
                            <div className="flex items-center gap-1">
                              {hasEcartCie && (
                                <span
                                  title={s.anomalies_compagnie.map(a => `${a.label} : attendu ${a.attendu} / relevé ${a.releve}`).join('\n')}
                                  data-testid="badge-ecart-cie"
                                >
                                  <AlertTriangle size={12} className="text-orange-500 shrink-0" />
                                </span>
                              )}
                              <span className="font-mono text-[11px] text-indigo-600 font-bold">{s.numero_sinistre_client || '--'}</span>
                            </div>
                          </td>
                          <td className="px-2 py-2" onClick={e => e.stopPropagation()}>
                            <BadgesCell sinistre={s} onUpdated={fetchSinistres} />
                          </td>
                          <td className="px-2 py-2" onClick={() => navigate(`/c2/sinistre/${s.id}`)}>
                            {s.mode_gestion === 'amiable' || s.est_amiable ? (
                              <span className="text-xs font-bold text-slate-900">{[s.tiers_nom, s.tiers_prenom].filter(Boolean).join(' ') || '--'}</span>
                            ) : (
                              <span className="text-xs font-bold text-slate-900 font-mono">{s.numero_dossier_compagnie || '--'}</span>
                            )}
                          </td>
                          <td className="px-2 py-2" onClick={e => e.stopPropagation()}>
                            <InlineDateCell sinistre={s} field="date_fait" onUpdated={fetchSinistres} label="Date du sinistre" />
                          </td>
                          <td className="px-2 py-2" onClick={() => navigate(`/c2/sinistre/${s.id}`)}>
                            <span className="text-xs font-mono text-slate-900 font-bold">{immat}</span>
                          </td>
                          <td className="px-2 py-2" onClick={() => navigate(`/c2/sinistre/${s.id}`)} data-testid={`cell-actee-${s.id}`}>
                            {(() => { const o = RESPONSABILITE_ACTEE_OPTIONS.find(x => x.value === s.responsabilite_actee); return o && o.value
                              ? <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${o.color}`}><span className={`w-1.5 h-1.5 rounded-full ${o.dot}`} />{o.label}</span>
                              : <span className="text-[10px] text-slate-300">non actée</span> })()}
                          </td>
                          <td className="px-2 py-2" onClick={e => e.stopPropagation()}>
                            <div className="flex items-center gap-1">
                              {noteHex && !hasNote && (
                                <span
                                  className="w-4 h-4 rounded-full shrink-0 ring-2 ring-white shadow"
                                  style={{ backgroundColor: noteHex }}
                                  data-testid="note-pastille"
                                />
                              )}
                              <NoteGestionnaireCell sinistre={s} onUpdated={fetchSinistres} grande />
                            </div>
                          </td>
                          <td className="px-2 py-2" onClick={() => navigate(`/c2/sinistre/${s.id}`)}>
                            {hasExpertise ? (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-purple-100 text-purple-700" data-testid="expertise-badge">
                                Exp.
                              </span>
                            ) : (
                              <span className="text-[10px] text-slate-300">&mdash;</span>
                            )}
                          </td>
                          <td className="px-2 py-2" onClick={e => e.stopPropagation()} data-testid={`cell-statut-${s.id}`}>
                            <InlineSelectCell sinistre={s} field="statut"
                              options={STATUT_FLOTTE_OPTIONS.some(o => o.value === s.statut) ? STATUT_FLOTTE_OPTIONS : [{ value: s.statut, label: st.label, color: st.color, dot: st.dot }, ...STATUT_FLOTTE_OPTIONS]}
                              onUpdated={fetchSinistres} placeholder="--" />
                          </td>
                          <td className="px-1 py-2" onClick={e => e.stopPropagation()}>
                            <button onClick={() => setEditingSinistre(s)} className="p-1 hover:bg-indigo-50 rounded transition-colors" title="Editer" data-testid="sinistre-edit-btn">
                              <Edit3 size={12} className="text-slate-400 hover:text-indigo-600" />
                            </button>
                          </td>
                        </tr>
                      )
                    }

                    // Standard row
                    const noteHexStd = s.note_couleur ? (
                      s.note_couleur === 'jaune' ? '#FCD34D' :
                      s.note_couleur === 'marron' ? '#A16207' :
                      s.note_couleur === 'rose' ? '#F472B6' :
                      s.note_couleur === 'violet' ? '#A78BFA' :
                      s.note_couleur === 'vert' ? '#34D399' :
                      s.note_couleur === 'bleu' ? '#60A5FA' : '#9CA3AF'
                    ) : null
                    return (
                      <tr 
                        key={s.id} 
                        className={`border-b border-slate-100 hover:bg-indigo-50/50 cursor-pointer transition-colors ${i % 2 ? 'bg-slate-50/30' : ''} ${selected.has(s.id) ? 'bg-indigo-50' : ''}`}
                        data-testid="sinistre-row"
                      >
                        <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selected.has(s.id)}
                            onChange={() => toggleSelect(s.id)}
                            className="rounded border-slate-300"
                            data-testid="sinistre-checkbox"
                          />
                        </td>
                        <td className="px-1 py-2" onClick={e => e.stopPropagation()}>
                          <FlagAttenteCell sinistre={s} onUpdated={fetchSinistres} />
                        </td>
                        <td className="px-3 py-3" onClick={() => navigate(`/c2/sinistre/${s.id}`)}>
                          <InlineEditLieu sinistre={s} onUpdated={fetchSinistres} />
                        </td>
                        <td className="px-3 py-3" onClick={() => navigate(`/c2/sinistre/${s.id}`)}>
                          <span className="text-xs text-slate-500 font-mono">{s.numero_dossier_compagnie || '--'}</span>
                        </td>
                        <td className="px-3 py-3" onClick={() => navigate(`/c2/sinistre/${s.id}`)}>
                          <p className="text-xs font-medium text-slate-800">{s.tenants?.nom}</p>
                        </td>
                        <td className="px-3 py-3" onClick={() => navigate(`/c2/sinistre/${s.id}`)}>
                          <p className="text-xs text-slate-600 max-w-[140px] truncate">{s.nature_sinistre || s.type_sinistre}</p>
                          {s.polices?.libelle && (
                            <p className="text-[10px] text-indigo-500 max-w-[140px] truncate mt-0.5">{s.polices.libelle}</p>
                          )}
                        </td>
                        <td className="px-3 py-3" onClick={() => navigate(`/c2/sinistre/${s.id}`)}>
                          {mg ? (
                            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${mg.color}`}>{mg.label}</span>
                          ) : (
                            <span className="text-[10px] text-slate-400">N/D</span>
                          )}
                        </td>
                        <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center gap-2">
                            {noteHexStd && (
                              <span
                                className="w-4 h-4 rounded-full shrink-0 ring-2 ring-white shadow-sm"
                                style={{ backgroundColor: noteHexStd }}
                                data-testid="note-pastille-std"
                              />
                            )}
                            <NoteGestionnaireCell sinistre={s} onUpdated={fetchSinistres} />
                          </div>
                        </td>
                        <td className="px-3 py-3" onClick={() => navigate(`/c2/sinistre/${s.id}`)}>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${st.color}`}>
                            <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${st.dot}`} />
                            {st.label}
                          </span>
                        </td>
                        <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                          <InlineDateCell sinistre={s} field="date_fait" onUpdated={fetchSinistres} label="Date du sinistre" />
                        </td>
                        <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                          <button
                            onClick={() => setEditingSinistre(s)}
                            className="p-1.5 hover:bg-indigo-50 rounded transition-colors"
                            title="Editer"
                            data-testid="sinistre-edit-btn"
                          >
                            <Edit3 size={14} className="text-slate-400 hover:text-indigo-600" />
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
      </>
    </div>
  )
}
